import { Reveal } from "@/components/motion/Reveal";
import { brand } from "@/data/content";

const CASCADE = [
  "A launch could become an experience.",
  "An experience could become content.",
  "Content could spark conversation.",
  "Conversation could generate attention.",
  "Attention, directed well, could build a brand.",
];

export default function AboutStory() {
  return (
    <div data-testid="about-story">
      {/* Lead */}
      <section className="mx-auto max-w-[1600px] px-5 pt-14 sm:pt-16 md:pt-20 lg:pt-24 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="grid gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                Our story
              </p>
              <h2 className="mt-5 font-display text-3xl font-extrabold leading-[1.02] tracking-tight text-ink md:text-5xl">
                Finding the method
                <br />
                <span className="font-serif font-normal italic text-ink/70">in the madness.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="mt-10 max-w-2xl font-display text-2xl font-extrabold leading-tight text-ink md:text-3xl">
                Every brand begins with an ambition.
              </p>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">
                Sometimes it&rsquo;s scribbled on a piece of paper. Sometimes it&rsquo;s born across a
                conference table. Sometimes it lives only in the mind of an entrepreneur who knows
                exactly what they want to build, yet has no idea how to make the world see it.
              </p>
              <p className="mt-8 max-w-2xl font-serif text-2xl italic leading-snug text-ink md:text-3xl">
                HashTag Marketing was built for that moment.
              </p>
            </Reveal>
          </div>

          <aside className="lg:col-span-3 lg:col-start-10">
            <Reveal delay={0.12}>
              <div className="border-t border-line pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Founded</p>
                <p className="mt-2 font-display text-2xl font-extrabold text-ink">August 2013</p>
              </div>
              <div className="mt-8 border-t border-line pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Based in</p>
                <p className="mt-2 font-display text-2xl font-extrabold text-ink">{brand.city}</p>
              </div>
              <div className="mt-8 border-t border-line pt-6">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Directors</p>
                <p className="mt-2 text-base leading-relaxed text-muted">
                  Gulnar Virk
                  <br />
                  Yuvraj Singh Virk
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {/* Gulnar */}
      <section className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-muted">
          <Reveal>
            <p>
              The story begins with <span className="font-semibold text-ink">Gulnar Virk</span>, a
              marketer by education and an entrepreneur by instinct.
            </p>
          </Reveal>
          <Reveal delay={0.04}>
            <p>
              After studying marketing at IBS Hyderabad, she stepped into a world where brands were no
              longer built through advertisements alone. They were being experienced in launches,
              conversations, events, culture, and, increasingly, on screens.
            </p>
          </Reveal>
          <Reveal delay={0.04}>
            <p>
              Her journey took her through major brands and ambitious campaigns, from luxury and
              lifestyle launches to large scale public initiatives. And somewhere between the strategy
              decks and the launch nights came a simple realization.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.06}>
          <blockquote className="mt-10 max-w-3xl border-l-2 border-accent pl-6 font-serif text-2xl italic leading-snug text-ink md:text-3xl">
            A great brand cannot be manufactured in a day. It has to be built, experienced and
            remembered.
          </blockquote>
        </Reveal>
        <Reveal delay={0.04}>
          <p className="mt-10 max-w-3xl text-lg leading-relaxed text-muted">
            Branding, as Gulnar would put it, is a journey, not a destination. That belief became the
            foundation of HashTag Marketing.
          </p>
        </Reveal>
      </section>

      {/* Founding + India's landscape */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <div className="grid gap-8 sm:gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                  August 2013 · Gurgaon
                </p>
                <p className="mt-5 font-display text-3xl font-extrabold leading-tight text-ink md:text-4xl">
                  HashTag Marketing formally took shape, with Gulnar and Yuvraj Singh Virk as its
                  directors.
                </p>
                <p className="mt-6 font-serif text-xl italic text-ink/70">
                  The timing could hardly have been better.
                </p>
              </Reveal>
            </div>
            <div className="max-w-2xl space-y-6 text-lg leading-relaxed text-muted lg:col-span-6 lg:col-start-7">
              <Reveal>
                <p>
                  India&rsquo;s marketing landscape was shifting fast. Audiences moved fluidly between
                  physical and digital worlds. Social media was turning them from viewers into
                  participants. Startups were challenging incumbents, events were becoming experiences,
                  and PR, advertising, content and digital could no longer afford to sit in separate
                  rooms.
                </p>
              </Reveal>
              <Reveal delay={0.04}>
                <p className="font-serif text-2xl italic leading-snug text-ink">
                  Brands needed someone to connect the dots. HashTag set out to be that connector.
                </p>
              </Reveal>
              <Reveal delay={0.04}>
                <p>
                  Gulnar brought an entrepreneur&rsquo;s eye for brands, experiences and execution.
                  Yuvraj brought a decade across communications, public affairs, digital platforms and
                  campaign strategy.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Proposition + cascade */}
      <section className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-muted">
            Together, they built an agency around a simple proposition.
          </p>
          <p className="mt-6 font-display text-4xl font-extrabold leading-tight tracking-tight text-ink md:text-6xl">
            Don&rsquo;t just make a brand visible.
            <br />
            <span className="text-accent">Make it matter.</span>
          </p>
        </Reveal>
        <Reveal delay={0.06}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted sm:mt-10 md:mt-12">
            That meant refusing to treat marketing as a set of disconnected services.
          </p>
        </Reveal>
        <ol className="mt-8 max-w-2xl space-y-3">
          {CASCADE.map((line, i) => (
            <Reveal key={line} delay={0.05 + i * 0.06}>
              <li className="flex items-baseline gap-4 border-b border-line pb-3">
                <span className="font-serif text-lg italic text-accent">0{i + 1}</span>
                <span className="text-xl font-medium text-ink md:text-2xl">{line}</span>
              </li>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={0.08}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted sm:mt-10 md:mt-12">
            So HashTag grew across the entire spectrum, from brand strategy, content and design to
            digital marketing, PR and research, and on to events, exhibitions and activations.
          </p>
          <p className="mt-6 font-display text-2xl font-extrabold text-ink md:text-3xl">
            Different disciplines. One story.
          </p>
        </Reveal>
      </section>

      {/* Through line + the method line */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-muted">
            <Reveal>
              <p>
                Over the years, the industries changed. The briefs changed. Platforms and audiences
                changed. The fundamental challenge didn&rsquo;t.
              </p>
            </Reveal>
            <Reveal delay={0.04}>
              <p>
                Every client arrived with their own version of chaos. An idea that needed positioning,
                a product that needed launching, a brand that needed reinvention, an audience that
                needed reaching, or a story that simply wasn&rsquo;t being heard.
              </p>
            </Reveal>
            <Reveal delay={0.04}>
              <p>HashTag&rsquo;s job was to find the thread running through it all.</p>
            </Reveal>
          </div>
          <Reveal delay={0.08}>
            <p
              data-testid="about-method-line"
              className="mt-8 font-serif text-4xl sm:mt-10 md:mt-12 font-normal italic leading-[1.05] tracking-tight text-ink md:text-6xl"
            >
              Find the method in the madness.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HashTag metaphor */}
      <section className="mx-auto max-w-[1600px] px-5 pb-14 sm:pb-16 md:pb-20 lg:pb-24 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-muted">
          <Reveal>
            <p>
              Perhaps that&rsquo;s why the name fits so well. A hashtag takes thousands of scattered
              voices and gives them a common thread.
            </p>
          </Reveal>
          <Reveal delay={0.04}>
            <p>
              Great marketing does much the same. It takes strategy, creativity, experiences, media,
              culture and people, and turns them into one conversation worth joining.
            </p>
          </Reveal>
          <Reveal delay={0.04}>
            <p className="text-ink">
              More than a decade on, that remains the idea at the heart of HashTag Marketing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Closing philosophy: standalone visual quote block */}
      <section data-testid="about-philosophy" className="bg-ink text-bone">
        <div className="mx-auto max-w-[1600px] px-5 py-20 sm:py-24 md:py-28 lg:py-36 sm:px-8 md:px-10 lg:px-12 xl:px-16">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
              The idea at the heart of it
            </p>
          </Reveal>
          <blockquote className="mt-10 font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl md:text-6xl">
            <Reveal>
              <span className="block">Ideas create attention.</span>
            </Reveal>
            <Reveal delay={0.08}>
              <span className="block text-bone/50">Strategy gives them direction.</span>
            </Reveal>
            <Reveal delay={0.16}>
              <span className="block">Execution gives them life.</span>
            </Reveal>
            <Reveal delay={0.24}>
              <span className="block text-accent">And consistency turns them into brands.</span>
            </Reveal>
          </blockquote>
          <Reveal delay={0.3}>
            <p className="mt-10 max-w-2xl text-lg leading-relaxed text-bone/70 sm:mt-12 md:mt-14 md:text-xl">
              HashTag exists somewhere in between all four. Not merely creating campaigns, but
              connecting the dots that make a brand impossible to ignore.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
